/** Pure helpers for the editor's word list. */

export interface EditorWord {
  /** Stable key for React and drag-and-drop (not the server id: new words have none yet). */
  key: string;
  lemma: string;
  context: string;
  /** Card content exists in the library. Undefined for words not saved yet. */
  ready?: boolean;
}

export function normalizeLemma(lemma: string): string {
  return lemma.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

/**
 * The server's rule for a deck word (`validWord` in supabase/functions/_shared/card-rules.ts; a
 * test keeps the two identical): starts with a letter or "(", then only letters, spaces and
 * ' . , ! ? - / ( ), at most 5 words and 80 characters.
 */
const WORD_RE = /^[\p{L}(][\p{L}\s'’.,!?()/-]*$/u;
export const MAX_LEMMA_LENGTH = 80;
export const MAX_LEMMA_WORDS = 5;

/** Why `lemma` cannot be a deck word, in words the owner can act on; null when it is fine. */
export function wordProblem(lemma: string): string | null {
  const word = lemma.trim().replace(/\s+/g, ' ');
  if (!word) return 'It is empty.';
  if (word.length > MAX_LEMMA_LENGTH) return `It is longer than ${MAX_LEMMA_LENGTH} characters.`;
  if (word.split(' ').length > MAX_LEMMA_WORDS) {
    return `It has more than ${MAX_LEMMA_WORDS} words. Put a sentence after a tab instead, as context.`;
  }
  if (!/^[\p{L}(]/u.test(word) || !/\p{L}/u.test(word))
    return 'It must start with a letter (no numbers, bullets or dashes first).';
  if (!WORD_RE.test(word)) {
    const bad = [...new Set([...word].filter((ch) => !/[\p{L}\s'’.,!?()/-]/u.test(ch)))];
    return `It contains ${bad.map((ch) => `“${ch}”`).join(' ')}. Only letters, spaces and ' . , ! ? - / ( ) are allowed.`;
  }
  return null;
}

export interface InvalidLine {
  /** 1-based line number in the pasted text. */
  line: number;
  /** The whole line as pasted (word and, if any, its context), so it can be put back for fixing. */
  text: string;
  lemma: string;
  reason: string;
}

export interface ParsedWords {
  words: { lemma: string; context: string }[];
  /** Lines dropped because the word was already in the deck or earlier in the paste. */
  duplicates: string[];
  /** Lines that cannot be deck words, with the reason. They are not added. */
  invalid: InvalidLine[];
}

/**
 * One word or phrase per line; an optional context sentence follows a tab:
 * `Schiff<TAB>Das Schiff fährt nach Hamburg.` Blank lines are ignored, duplicates (also of
 * `existing`) are reported and dropped, the first occurrence wins. Lines that cannot be deck
 * words (see wordProblem) are reported with the reason and not added.
 */
export function parseWordList(text: string, existing: string[] = []): ParsedWords {
  const seen = new Set(existing.map(normalizeLemma));
  const words: ParsedWords['words'] = [];
  const duplicates: string[] = [];
  const invalid: InvalidLine[] = [];
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    if (!rawLine.trim()) continue;
    const tab = rawLine.indexOf('\t');
    const lemma = (tab === -1 ? rawLine : rawLine.slice(0, tab)).trim().replace(/\s+/g, ' ');
    const context = tab === -1 ? '' : rawLine.slice(tab + 1).trim();
    const reason = wordProblem(lemma);
    if (reason) {
      invalid.push({ line: i + 1, text: rawLine.trim(), lemma, reason });
      continue;
    }
    const norm = normalizeLemma(lemma);
    if (seen.has(norm)) {
      duplicates.push(lemma);
      continue;
    }
    seen.add(norm);
    words.push({ lemma, context });
  }
  return { words, duplicates, invalid };
}

/** Returns a copy with the item at `from` moved to index `to` (clamped). */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = items.slice();
  if (from < 0 || from >= next.length) return next;
  const target = Math.min(Math.max(to, 0), next.length - 1);
  const [item] = next.splice(from, 1);
  next.splice(target, 0, item);
  return next;
}

let counter = 0;
export function newKey(): string {
  counter += 1;
  return `w${Date.now().toString(36)}-${counter}`;
}
